/**
 * Masks the local-part of an email address, preserving only the first character
 * and the domain. Keeps enough context to distinguish between accounts without
 * exposing PII in shared surfaces (e.g. notification inboxes).
 *
 * Example: `john.doe@example.com` → `j***@example.com`
 */
export function maskEmail(email: string): string {
  const atIndex = email.indexOf('@');
  if (atIndex <= 0) return email;
  const local = email.slice(0, atIndex);
  const domain = email.slice(atIndex); // includes the '@'
  const firstChar = local[0] ?? '';
  const masked = local.length === 1 ? firstChar : `${firstChar}***`;
  return `${masked}${domain}`;
}

/** Regex that matches RFC-5321-style email addresses in free-form text. */
const EMAIL_PATTERN = /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g;

/**
 * Replaces every email address found in a free-form string with its masked
 * equivalent. Safe to call on any string — returns the input unchanged if no
 * email address is present.
 *
 * Example: `"Invited p.user@dyleris.com to join"` →
 *          `"Invited p***@dyleris.com to join"`
 */
export function maskEmailsInText(text: string): string {
  return text.replace(EMAIL_PATTERN, maskEmail);
}

/**
 * Masks an email's local-part as `{first}****{middle}*****{last}@domain`,
 * keeping three characters as a recognisable hint with fixed-length masks so
 * the real length isn't revealed. The middle is the central character, not a
 * random one, so the same address always renders the same way.
 *
 * Example: `c-ravi.maurya@lily.ai` → `c****.*****a@lily.ai`
 */
export function maskEmailWithHint(email: string): string {
  const atIndex = email.indexOf('@');
  if (atIndex <= 0) return email;
  const local = email.slice(0, atIndex);
  const domain = email.slice(atIndex); // includes the '@'
  const first = local.charAt(0);
  if (local.length === 1) return `${first}****${domain}`;
  const last = local.charAt(local.length - 1);
  if (local.length === 2) return `${first}****${last}${domain}`;
  const middle = local.charAt(Math.floor(local.length / 2));
  return `${first}****${middle}*****${last}${domain}`;
}
